import {
  ApplicationRef,
  Component,
  createComponent,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  LngLatBounds,
  Map,
  Marker,
  MarkerOptions,
  NavigationControl,
  Popup,
} from 'maplibre-gl';
import { createMap } from 'maplibre-gl-js-amplify';
import {
  LocationType,
  LocationTypeViewModel,
  ShipmentRouteMapPopupComponent,
} from './shipment-route-map-popup/shipment-route-map-popup.component';
import {
  arrivalLocationText,
  createCustomMarker,
  endLocationText,
  startLocationText,
} from '@utils/marker-util';

/** ジオフェンスのポリゴン */
export type ShipmentGeofencePolygonViewModel = {
  /** 緯度 */
  latitude: number;
  /** 経度 */
  longitude: number;
};

/** 配送位置情報のポリゴン形式ジオフェンス */
export type ShipmentPolygonGeofenceViewModel = {
  /** ジオフェンスタイプ */
  geofenceType: 'polygon';
  /** ポリゴンリスト */
  polygonList: ShipmentGeofencePolygonViewModel[];
};

/** 配送位置情報の円形式ジオフェンス */
export type ShipmentCircleGeofenceViewModel = {
  /** ジオフェンスタイプ */
  geofenceType: 'circle';
  /** 円の半径（元の指定した値） */
  circleRadius: number;
  /** 円の半径の単位 */
  circleRadiusUnit: string;
  /** 円の半径（メートル単位） */
  circleRadiusInMeters: number;
};

/** 配送位置情報のジオフェンス */
export type ShipmentGeofenceViewModel =
  | ShipmentCircleGeofenceViewModel
  | ShipmentPolygonGeofenceViewModel;

/** 配送位置情報のレスポンス内容 */
export type ShipmentLocationViewModel = {
  /** 位置情報ID */
  locationId: string;
  /** 位置情報名 */
  locationName: string;
  /** 緯度 */
  latitude: number;
  /** 経度 */
  longitude: number;
  /** 配送もしくはルート設定で使用されている位置情報か */
  isUse?: boolean;
  /** 配送中のルート設定で使用されている位置情報か */
  isShipping?: boolean;
  /** 郵便番号 */
  postalCode?: string;
  /** 住所 */
  address?: string;
  /** ジオフェンス */
  geofence?: ShipmentGeofenceViewModel;
};

/** 配送ルートのViewModel */
export type ShipmentRouteViewModel = {
  /** ルートID */
  routeId: string;
  /** ルート名 */
  routeName: string;
  /** 出発地 */
  startLocation?: ShipmentLocationViewModel;
  /** 経由地リスト */
  viaLocationList?: ShipmentLocationViewModel[];
  /** 到着地 */
  arrivalLocation?: ShipmentLocationViewModel;
  /** 永続化 */
  permanent: boolean;
  /** 配送設定で使用されているか */
  isUse: boolean;
};

/** 出発地もしくは到着地に変更するときに発火する要素の型 */
export type StartOrArrivalLocationUpsertType = {
  /** 位置情報ID */
  locationId: string;
  /** 経由地のインデックス(undefinedの場合は経由地でないことを意味する) */
  index?: number;
};

/** 経由地に変更するときに発火する要素の型 */
export type ViaLocationUpsertType = {
  /** 位置情報ID */
  locationId: string;
  /** 元の位置情報タイプ(出発地か到着地かそれ以外) */
  beforeLocationType: LocationType;
};

@Component({
  selector: 'app-shipment-route-map',
  standalone: true,
  imports: [],
  templateUrl: './shipment-route-map.component.html',
  styleUrl: './shipment-route-map.component.scss',
})
export class ShipmentRouteMapComponent implements OnInit, OnChanges {
  /** ルート */
  @Input() route: ShipmentRouteViewModel | undefined;
  /** mapのHTML要素のid */
  @Input() mapId: string = 'map';
  /** 追加の位置情報リスト */
  @Input() additionalLocationList: ShipmentLocationViewModel[] = [];
  /** ポップアップにボタンを表示するか */
  @Input() displayButtonOnPopup: boolean = false;
  /** 経由地フォーム数 */
  @Input() public numberOfViaLocationForm: number = 1;

  /** 押されたボタンの位置情報タイプ */
  @Output() startLocationUpsert =
    new EventEmitter<StartOrArrivalLocationUpsertType>();
  /** 押されたボタンの位置情報タイプ */
  @Output() viaLocationUpsert = new EventEmitter<ViaLocationUpsertType>();
  /** 押されたボタンの位置情報タイプ */
  @Output() arrivalLocationUpsert =
    new EventEmitter<StartOrArrivalLocationUpsertType>();

  private map!: Map;
  private markerList: Marker[] = [];
  private additionalMarkers: Marker[] = [];

  private routeColor = '#606060';

  private popupList: Popup[] = [];

  constructor(private applicationRef: ApplicationRef) {}

  async ngOnInit() {
    this.map = await createMap({
      container: this.mapId,
      center: [139.767, 35.681],
      zoom: 11,
      minZoom: 1,
    });

    this.map.addControl(
      new NavigationControl({
        visualizePitch: true,
      }),
    );

    // arrowhead
    const arrowHeadImage = await this.createArrowHeadImage(40, this.routeColor);
    this.map.addImage('arrow-head', arrowHeadImage);

    // 地図初期化完了前に入力が変化する場合があるため、準備完了後に最新状態を再反映する。
    this.setMainLayers(this.route).then();
    this.setAdditionalLayers().then();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['route'] !== undefined) {
      // マップに情報を表示する
      const route: ShipmentRouteViewModel | undefined =
        changes['route'].currentValue;
      this.setMainLayers(route).then();
    }

    // 登録済み位置情報を表示
    if (changes['additionalLocationList'] !== undefined) {
      // マップに登録したい情報・登録済み位置情報を表示する
      this.setAdditionalLayers().then();
    }
  }

  private async setMainLayers(
    route: ShipmentRouteViewModel | undefined,
    pan = true,
  ) {
    // マップが表示するまで待機
    if (!(await this.waitMapLoaded())) {
      return;
    }

    // マーカー表示
    const markers = this.setMarkers(route);

    // ルート表示
    this.drawRoutes(markers);

    // PANする
    if (pan) {
      this.panToMarkers(markers);
    }
  }

  private async setAdditionalLayers() {
    // マップが表示するまで待機
    if (!(await this.waitMapLoaded())) {
      return;
    }

    // 追加のマーカー表示
    this.setAdditionalMarkers();
  }

  private setMarkers(route: ShipmentRouteViewModel | undefined): Marker[] {
    // 初期化
    this.markerList.forEach((marker) => marker.remove());
    this.markerList = [];

    this.popupList.forEach((popup) => popup.remove());
    this.popupList = [];

    if (!route) return [];

    // 出発地追加
    const startLocation = route.startLocation;
    if (startLocation) {
      // カスタムマーカーの要素を作成
      const markerElement = createCustomMarker(startLocationText);

      const longitude = startLocation.longitude;
      const latitude = startLocation.latitude;
      const marker = new Marker({ element: markerElement })
        .setLngLat({ lat: latitude, lng: longitude })
        .addTo(this.map);
      this.markerList.push(marker);

      this.displayButtonOnPopup
        ? this.setPopupForEdit(marker, startLocation)
        : this.setPopup(marker, startLocation);
    }

    // 経由地追加
    const viaLocationList = route.viaLocationList ?? [];
    for (const [index, viaLocation] of viaLocationList.entries()) {
      // カスタムマーカーの要素を作成
      const viaNumber = index + 1;
      const displayText = `${arrivalLocationText}${viaNumber}`;
      const markerElement = createCustomMarker(displayText);

      const longitude = viaLocation.longitude;
      const latitude = viaLocation.latitude;
      const marker = new Marker({ element: markerElement })
        .setLngLat({ lat: latitude, lng: longitude })
        .addTo(this.map);
      this.markerList.push(marker);

      this.displayButtonOnPopup
        ? this.setPopupForEdit(marker, viaLocation)
        : this.setPopup(marker, viaLocation);
    }

    // 到着地追加
    const arrivalLocation = route.arrivalLocation;
    if (arrivalLocation) {
      // カスタムマーカーの要素を作成
      const markerElement = createCustomMarker(endLocationText);

      const longitude = arrivalLocation.longitude;
      const latitude = arrivalLocation.latitude;
      const marker = new Marker({ element: markerElement })
        .setLngLat({ lat: latitude, lng: longitude })
        .addTo(this.map);
      this.markerList.push(marker);

      this.displayButtonOnPopup
        ? this.setPopupForEdit(marker, arrivalLocation)
        : this.setPopup(marker, arrivalLocation);
    }

    return this.markerList;
  }

  private drawRoutes(markers: Marker[]) {
    // 初期化
    if (this.map.getLayer('route') !== undefined) {
      this.map.removeLayer('route');
    }
    if (this.map.getLayer('direction') !== undefined) {
      this.map.removeLayer('direction');
    }
    if (this.map.getSource('route') !== undefined) {
      this.map.removeSource('route');
    }

    // マーカーの座標を取得
    const markerCoordinates = markers.map((marker) => {
      const lngLat = marker.getLngLat();
      return [lngLat.lng, lngLat.lat];
    });

    const route = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: markerCoordinates,
          },
        },
      ],
    };

    this.map.addSource('route', {
      type: 'geojson',
      data: route,
    });

    this.map.addLayer({
      id: 'route',
      type: 'line',
      source: 'route',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': this.routeColor,
        'line-width': 5,
      },
    });

    this.map.addLayer({
      id: 'direction',
      type: 'symbol',
      source: 'route',
      layout: {
        'symbol-placement': 'line',
        'icon-image': 'arrow-head',
        'icon-offset': [15, 0],
        'symbol-spacing': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          100,
          20,
          500,
        ],
      },
    });
  }

  private setAdditionalMarkers(): void {
    // 初期化
    this.additionalMarkers.forEach((marker) => marker.remove());
    this.additionalMarkers = [];

    this.popupList.forEach((popup) => popup.remove());
    this.popupList = [];

    const additionalLocationList = this.additionalLocationList;

    if (!additionalLocationList) return;

    for (const additionalLocation of additionalLocationList) {
      const longitude = additionalLocation.longitude;
      const latitude = additionalLocation.latitude;

      // 位置情報がない場合はマーカーを表示しないようにガード
      if (longitude === undefined || latitude === undefined) {
        continue;
      }

      const markerOptions: MarkerOptions = {
        color: '#FFD700',
      };
      const marker = new Marker(markerOptions)
        .setLngLat([longitude, latitude])
        .addTo(this.map);
      this.additionalMarkers.push(marker);

      // ポップアップを設定
      this.setPopupForEdit(marker, additionalLocation);
    }
  }

  private panToMarkers(markers: Marker[]) {
    if (markers.length < 1) {
      this.map.setCenter([139.767, 35.681]);
      this.map.setZoom(11);
      return;
    }

    // マーカーの座標を取得
    const markerCoordinates = markers.map((marker) => marker.getLngLat());

    // マーカーの座標を包括する範囲を計算
    const bounds = new LngLatBounds();
    markerCoordinates.forEach((coord) => bounds.extend(coord));

    // マップをPANしてマーカーが見えるようにする
    this.map.fitBounds(bounds, { padding: 50, maxZoom: 16, animate: false });
  }

  /** 新規追加・更新時に使用するポップアップを設定する */
  private setPopupForEdit(
    marker: Marker | undefined,
    location: ShipmentLocationViewModel,
  ) {
    // マーカーホバー時に表示されるポップアップの挙動設定
    const popup = new Popup({
      closeButton: true,
      closeOnClick: false,
    });

    // マーカーホバー時の挙動設定
    marker?.getElement().addEventListener('mouseenter', () => {
      this.popupList.forEach((popup) => popup.remove());
      this.popupList.push(popup);

      const locationId = location.locationId;

      const popupNode = document.createElement('div');
      popupNode.innerHTML = `<div id="${locationId}-popup"></div>`;

      const longitude = location.longitude;
      const latitude = location.latitude;

      if (longitude && latitude) {
        popup
          .setLngLat([longitude, latitude])
          .setDOMContent(popupNode)
          .setOffset([0, -10])
          .addTo(this.map);

        this.createPointDescription(
          locationId,
          location.locationName,
          latitude,
          longitude,
          location.address,
          location.geofence,
        );

        requestAnimationFrame(() => {
          this.adjustPopupPosition(popup);
        });
      }
    });
  }

  /** 一覧画面で使用するポップアップを設定する */
  private setPopup(
    marker: Marker | undefined,
    location: ShipmentLocationViewModel,
  ) {
    // マーカーホバー時に表示されるポップアップの挙動設定
    const popup = new Popup({
      closeButton: false,
      closeOnClick: false,
    });

    // マーカーホバー時の挙動設定
    marker?.getElement().addEventListener('mouseenter', () => {
      const locationId = location.locationId;

      const popupNode = document.createElement('div');
      popupNode.innerHTML = `<div id="${locationId}-popup"></div>`;

      const longitude = location.longitude;
      const latitude = location.latitude;

      if (longitude && latitude) {
        popup
          .setLngLat([longitude, latitude])
          .setDOMContent(popupNode)
          .setOffset([0, -10])
          .addTo(this.map);

        this.createPointDescription(
          locationId,
          location.locationName,
          latitude,
          longitude,
          location.address,
          location.geofence,
        );
        requestAnimationFrame(() => {
          this.adjustPopupPosition(popup);
        });
      }
    });

    marker?.getElement().addEventListener('mouseleave', () => {
      marker.getElement().style.cursor = '';
      popup.remove();
    });
  }

  /**
   * マーカーのツールチップポップアップのテキストを生成する
   * @param locationName
   * @param latitude
   * @param longitude
   * @param address
   * @param geofence
   * @returns ポップアップで表示するHTML要素
   */
  private createPointDescription(
    locationId: string,
    locationName: string,
    latitude: number,
    longitude: number,
    address?: string,
    geofence?: ShipmentGeofenceViewModel,
  ): void {
    let geofenceText: string = '';
    if (geofence?.geofenceType === 'circle') {
      geofenceText = `円  半径${geofence.circleRadius}${geofence.circleRadiusUnit}`;
    } else if (geofence?.geofenceType === 'polygon') {
      geofenceText = `フリー描画  ${geofence.polygonList.length - 1} 頂点`;
    }

    const host = document.getElementById(`${locationId}-popup`);

    // hostの型チェック
    if (!host) {
      return;
    }

    // 新規追加・編集画面での表示
    const startLocation = this.route?.startLocation;
    const arrivalLocation = this.route?.arrivalLocation;

    const viaLocationList = this.route?.viaLocationList;

    const popoverComponent = createComponent(ShipmentRouteMapPopupComponent, {
      hostElement: host,
      environmentInjector: this.applicationRef.injector,
    });

    this.applicationRef.attachView(popoverComponent.hostView);
    popoverComponent.changeDetectorRef.detectChanges();

    // ポップアップを表示する位置情報タイプ
    let locationType: LocationType = '';
    const viaLocationIndex = viaLocationList?.findIndex(
      (viaLocation) =>
        viaLocation.latitude === latitude &&
        viaLocation.longitude === longitude,
    );
    if (
      startLocation?.latitude === latitude &&
      startLocation.longitude === longitude
    ) {
      locationType = 'start';
    } else if (
      arrivalLocation?.latitude === latitude &&
      arrivalLocation.longitude === longitude
    ) {
      locationType = 'arrival';
    } else if (viaLocationIndex !== undefined && viaLocationIndex > -1) {
      locationType = 'via';
    }
    popoverComponent.setInput('locationType', locationType);
    popoverComponent.setInput('locationId', locationId);
    popoverComponent.setInput('locationName', locationName);
    popoverComponent.setInput('geofenceText', geofenceText);
    popoverComponent.setInput('address', address);
    popoverComponent.setInput(
      'displayButtonOnPopup',
      this.displayButtonOnPopup,
    );
    popoverComponent.setInput(
      'numberOfViaLocationForm',
      this.numberOfViaLocationForm,
    );

    // outputイベントの検知
    popoverComponent.instance.locationTypeButtonClicked.subscribe(
      (locationTypeEvent: LocationTypeViewModel) => {
        if (locationTypeEvent.locationType === 'start') {
          this.startLocationUpsert.emit({
            locationId: locationTypeEvent.locationId,
            index: viaLocationIndex,
          });
        } else if (locationTypeEvent.locationType === 'via') {
          this.viaLocationUpsert.emit({
            locationId: locationTypeEvent.locationId,
            beforeLocationType: locationType,
          });
        } else if (locationTypeEvent.locationType === 'arrival') {
          this.arrivalLocationUpsert.emit({
            locationId: locationTypeEvent.locationId,
            index: viaLocationIndex,
          });
        }
      },
    );
  }

  private async waitMapLoaded(): Promise<boolean> {
    if (this.map === undefined) {
      return false;
    } else if (this.map.loaded()) {
      return true;
    }

    return new Promise<boolean>((resolve) => {
      this.map.on('load', () => {
        resolve(true);
      });
    });
  }

  private createArrowHeadImage(
    size: number,
    color: string,
  ): Promise<ImageBitmap> {
    const data = `
      <svg width='${size}' height='${size}' viewBox='0 0 100 100' 
       xmlns='http://www.w3.org/2000/svg' version='1.1'><polygon fill='${color}' 
       stroke='gray' stroke-width='1' points='20,90 50,10 80,90 50,70' 
       transform='rotate(90 50 50)'/>
      </svg>
    `;
    return new Promise<ImageBitmap>((resolve) => {
      const img = new Image(size, size);
      img.src = 'data:image/svg+xml;base64,' + btoa(data);
      img.onload = () => resolve(createImageBitmap(img));
    });
  }

  /**
   * ツールチップが見切れている場合に位置を調整する
   * @param popup
   */
  private adjustPopupPosition(popup: Popup) {
    const popupElement = popup.getElement();
    const mapContainer = this.map.getContainer();

    // ポップアップの位置とサイズを取得
    if (popupElement && mapContainer) {
      const popupRect = popupElement.getBoundingClientRect();
      const mapRect = mapContainer.getBoundingClientRect();

      const isOutOfBounds = {
        top: popupRect.top < mapRect.top,
        bottom: popupRect.bottom > mapRect.bottom,
        left: popupRect.left < mapRect.left,
        right: popupRect.right > mapRect.right,
      };

      // 上側に見切れている場合、ポップアップを下に移動
      if (isOutOfBounds.top) {
        popup.setOffset([0, 10]);
        // 下側に見切れている場合、ポップアップを上に移動
      } else if (isOutOfBounds.bottom) {
        popup.setOffset([0, -10]);
      }

      // 左側に見切れている場合、右に移動
      if (isOutOfBounds.left) {
        popup.setOffset([10, 0]);
        // 右側に見切れている場合、左に移動
      } else if (isOutOfBounds.right) {
        popup.setOffset([-10, 0]);
      }
    }
  }
}
